/** @jsxImportSource solid-js */

import {
  Component,
  Show,
  createSignal,
  createEffect,
  createMemo,
  onMount,
  untrack,
} from 'solid-js';

import {
  IComment,
  IUser,
  useCommentState,
  useCommentApi,
} from '@tc-comments/providers';
import {
  ICommentAttachments,
  ICommentAttachmentDetails,
} from '@tc-comments/providers';
import { GetListallattachmentsResponse } from '@data-access/connect/tc-tcps';
import { AttachmentListInEdit } from '../AttachmentList/CommentAttachmentList';
import { defineSolidAuthImage, defineSolidImage } from '@tcweb/connect-ui';
import { translate } from '@fulcrum/lib';
import {
  AttachmentPicker,
  IAttachedFileList,
  ITab,
} from '@tc-attachment-picker/ui';

export interface ICommentProps {
  readonly currentUser: IUser;
  readonly commentIndex: number;
  readonly comment: IComment;
  onDeleteComment(index: number): void;
  onUpdateComment(index: number, description: string): Promise<boolean>;
  updateSpecificComment(commentId: string): void;
  readonly isDeleting: boolean;
  readonly isUpdating: boolean;
  readonly userRole: string;
  readonly projectId: string;
  readonly objectId: string | undefined;
  getCommentDetail(commentDetail: IComment): void;
  readonly isPreviewOn: boolean;
  readonly projectUser?: readonly IUser[];
  isCommentActionAllowed(commentAction: string): boolean;
  readonly policyToolTip: unknown;
  // AttachmentPicker integration props
  readonly allowedAttachmentTypes?: string;
  readonly allowedFileExtensions?: string[];
  readonly allowedUploadFileExt?: string[];
  readonly projectName?: string;
  // Note: accessToken and tcpsApiDomain are now accessed via useCommentState()
}

const Comment: Component<ICommentProps> = (props) => {
  // Get provider data for AttachmentPicker and API service
  const commentState = useCommentState();
  const commentApi = useCommentApi();
  const state = commentState();
  const accessToken = state.accessToken;
  const tcpsApiDomain = state.tcpsApiDomain;

  // Create the attachments mutations at component level (within provider context)
  const createCommentAttachmentsMutation =
    commentApi.createCommentAttachmentsMutation();
  const deleteCommentAttachmentsMutation =
    commentApi.deleteCommentAttachmentsMutation();

  // Initialize the web components
  onMount(() => {
    defineSolidAuthImage();
    defineSolidImage();
  });

  const [editMode, setEditMode] = createSignal(false);
  const [description, setDescription] = createSignal(
    untrack(() => props.comment.description)
  );
  const [updateCommentFailed, setUpdateCommentFailed] = createSignal(false);
  const [inProgress, setInProgress] = createSignal(false);
  const [showAttachmentPicker, setShowAttachmentPicker] = createSignal(false);
  const [attachments, setAttachments] = createSignal<ICommentAttachments[]>([]);
  const [attachmentsDetails, setAttachmentsDetails] = createSignal<
    ICommentAttachmentDetails[]
  >([]);
  const [uploadingAttachment, setUploadingAttachment] = createSignal(false);
  const [hasPersistedAttachments, setHasPersistedAttachments] = createSignal(
    untrack(() => props.comment.hasAttachment)
  );
  const [attachmentThumbnailBase64, setAttachmentThumbnailBase64] =
    createSignal<string>('');

  // Utility function to get user thumbnail from project users
  const getUserThumbnail = (tiduuid: string): string => {
    const fallbackUrl =
      'https://resources.stage.connect.trimble.com/thumb/user.png';

    if (!props.projectUser || !tiduuid) {
      return fallbackUrl; // Fallback image
    }

    const userDetails = props.projectUser.find(
      (user: IUser) => user.tiduuid === tiduuid
    );
    return userDetails?.avatar || fallbackUrl;
  };

  // Stable fallback image URL
  const fallbackImageUrl =
    'https://resources.stage.connect.trimble.com/thumb/user.png';

  // Create memos for user thumbnails
  const currentUserThumbnail = createMemo(() =>
    getUserThumbnail(props.currentUser.tiduuid)
  );
  const commentAuthorThumbnail = createMemo(() =>
    getUserThumbnail(props.comment.createdBy?.tiduuid ?? '')
  );

  // Create a reactive signal for the comment ID when hasAttachment is true OR when we have local attachments
  const getCommentIdForAttachments = createMemo(() => {
    return hasPersistedAttachments() || attachments().length > 0
      ? props.comment.id
      : undefined;
  });

  const commentAttachmentsQuery = commentApi.getCommentAttachmentsQuery(() =>
    untrack(getCommentIdForAttachments)
  );

  // Check for attachments on mount if hasAttachment is true
  onMount(() => {
    if (hasPersistedAttachments()) {
      // Fetch attachments immediately since hasAttachment is true
      commentAttachmentsQuery.refetch();
    }
  });

  // Update description when comment changes
  createEffect(() => {
    setDescription(props.comment.description);
  });

  // Update hasPersistedAttachments when comment changes
  createEffect(() => {
    setHasPersistedAttachments(props.comment.hasAttachment);
  });

  // Convert attachment thumbnail URL to base64 when data is available
  createEffect(() => {
    const attachmentData = commentAttachmentsQuery.data;
    if (
      attachmentData &&
      attachmentData.length > 0 &&
      attachmentData[0]?.thumbnailUrl
    ) {
      const thumbnailUrl = attachmentData[0].thumbnailUrl;
      // Convert the thumbnail URL to base64 using buildAuthImg
      buildAuthImg(thumbnailUrl)
        .then((base64Img) => {
          setAttachmentThumbnailBase64(base64Img);
        })
        .catch((error) => {
          console.error('Error loading attachment thumbnail:', error);
          setAttachmentThumbnailBase64(''); // Reset on error
        });
    } else {
      setAttachmentThumbnailBase64(''); // Reset if no attachment
    }
  });

  const isCurrentUser = (): boolean =>
    props.currentUser.tiduuid === props.comment.createdBy?.tiduuid;
  const isCommentChanged = (): boolean =>
    props.comment.description !== description() || attachments().length > 0;

  const elementHeightOffset = 24;
  const setElementHeight = (element: HTMLTextAreaElement): void => {
    if (!element) return;

    const commentSupportDiv = document.getElementById(
      'des-container' + props.comment.id
    );
    if (commentSupportDiv) {
      commentSupportDiv.innerHTML = element.value;
      element.style.height =
        commentSupportDiv.scrollHeight + elementHeightOffset + 'px';
    } else {
      // Fallback: auto-resize based on content
      element.style.height = 'auto';
      element.style.height = element.scrollHeight + 'px';
    }
  };

  const cancelEditing = (): void => {
    setEditMode(false);
    setDescription(props.comment.description);
    setUpdateCommentFailed(false);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 40 > 15 allowed; refactor later
  const updateCommentText = async (): Promise<void> => {
    if (description() && !props.isUpdating) {
      setInProgress(true);
      const desElement = document.getElementById(props.comment.id);
      if (desElement) {
        desElement.blur();
      }

      const commentIndex = props.commentIndex;
      try {
        // Step 1: Handle attachment replacement flow if we have new attachments
        if (attachments().length > 0) {
          // Step 1a: Get existing attachments for this comment
          let existingAttachments: GetListallattachmentsResponse = [];
          if (props.comment.hasAttachment) {
            try {
              existingAttachments = await commentApi.getCommentAttachments(
                props.comment.id
              );
            } catch (error) {
              console.error('Error fetching existing attachments:', error);
              // Continue with the flow even if we can't fetch existing attachments
            }
          }

          // Step 1b: Delete existing attachments if any
          if (existingAttachments.length > 0) {
            for (const existingAttachment of existingAttachments) {
              try {
                await deleteCommentAttachmentsMutation.mutateAsync({
                  commentId: props.comment.id,
                  attachmentId: existingAttachment.id,
                  attachmentType: existingAttachment.type || 'FILE',
                });
              } catch (deleteError) {
                console.error(
                  'Error deleting attachment:',
                  existingAttachment.id,
                  deleteError
                );
                // Continue with other deletions even if one fails
              }
            }
          }

          // Step 1c: Add new attachments
          try {
            // Filter for FILE attachments only
            const fileAttachments = attachments().filter(
              (att) => att.type === 'FILE'
            );

            if (fileAttachments.length > 0) {
              // Create attachment requests for the API
              const attachmentRequests = fileAttachments.map((attachment) => ({
                type: attachment.type,
                id: attachment.id,
              }));

              // Use the comment attachments API to persist the attachments
              await createCommentAttachmentsMutation.mutateAsync({
                commentId: props.comment.id,
                attachments: attachmentRequests,
              });
            }

            // Update the persisted attachments flag first
            setHasPersistedAttachments(true);

            // Emit custom event for attachment update
            const customEvent = new CustomEvent('comment-attachments-updated', {
              detail: {
                commentId: props.comment.id,
                attachments: fileAttachments,
              },
              bubbles: true,
            });
            document.dispatchEvent(customEvent);

            // Clear local attachments after successful save - delay to ensure smooth transition
            const attachmentClearDelay = 100;
            setTimeout(() => {
              setAttachments([]);
              setAttachmentsDetails([]);
            }, attachmentClearDelay);
          } catch (attachmentError) {
            console.error(
              'Error saving new comment attachments:',
              attachmentError
            );
            // Don't clear local attachments if save failed
            setInProgress(false);
            return;
          }
        }

        // Step 2: Update the comment text if edited
        if (props.comment.description !== description()) {
          const success = await props.onUpdateComment(
            commentIndex,
            description() ?? ''
          );
          if (success) {
            setEditMode(false);
            setUpdateCommentFailed(false);

            // Emit custom event
            const customEvent = new CustomEvent('comment-updated', {
              detail: {
                commentId: props.comment.id,
                newDescription: description(),
                commentIndex,
              },
              bubbles: true,
            });
            document.dispatchEvent(customEvent);
          } else {
            setUpdateCommentFailed(true);
          }
        } else {
          // If only attachments were updated, exit edit mode
          setEditMode(false);
          setUpdateCommentFailed(false);
        }

        // Step 3: Refresh attachment data
        // Immediate refresh to ensure UI updates
        await commentAttachmentsQuery.refetch();

        const refreshDelay = 200;
        await new Promise((resolve) => setTimeout(resolve, refreshDelay));
      } catch (error) {
        console.error('Error in updateCommentText:', error);
        setUpdateCommentFailed(true);
      }

      setInProgress(false);
    }
  };

  const onDescriptionUpdated = (e: Event): void => {
    const target = e.currentTarget as HTMLTextAreaElement;
    setDescription(target.value);
    setElementHeight(target);
  };

  const selectAction = (selectedAction: string): void => {
    switch (selectedAction) {
      case 'edit': {
        // Set edit mode and description first
        setEditMode(true);
        setDescription(props.comment.description);

        // Use setTimeout to ensure DOM is updated before focusing
        setTimeout(() => {
          const desElement = document.getElementById(
            props.comment.id
          ) as HTMLTextAreaElement;
          if (desElement) {
            setElementHeight(desElement);
            desElement.focus();
            // Set cursor to end of text
            desElement.setSelectionRange(
              desElement.value.length,
              desElement.value.length
            );
          }
        }, 0);
        break;
      }
      case 'delete': {
        deleteComment();
        break;
      }
    }
  };

  const deleteComment = (): void => {
    if (!props.isDeleting) {
      const customEvent = new CustomEvent('comment-delete-requested', {
        detail: {
          commentIndex: props.commentIndex,
          commentId: props.comment.id,
        },
        bubbles: true,
      });
      document.dispatchEvent(customEvent);

      props.onDeleteComment(props.commentIndex);
    }
  };

  const removeAttachment = (index: number): void => {
    const newAttachments = [...attachments()];
    const newAttachmentsDetails = [...attachmentsDetails()];
    newAttachments.splice(index, 1);
    newAttachmentsDetails.splice(index, 1);
    setAttachments(newAttachments);
    setAttachmentsDetails(newAttachmentsDetails);
  };

  // Utility function to build authenticated images
  const buildAuthImg = async (url: string): Promise<string> => {
    try {
      const authToken = accessToken ? `Bearer ${accessToken}` : '';

      const response = await fetch(url, {
        method: 'GET',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        headers: authToken ? { Authorization: authToken } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to fetch authenticated image');
      }

      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('buildAuthImg error:', error);
      throw error;
    }
  };

  const openAttachmentPicker = (): void => {
    setShowAttachmentPicker(true);
  };

  const handleAddAttachment = async (
    filesList: IAttachedFileList
    // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 19 > 15 allowed; refactor later
  ): Promise<void> => {
    const attachment = filesList;
    const attachmentDetail = attachment.selectedObject;
    const currentAttachments = attachments();
    const attachmentsPayload: ICommentAttachments[] = [];
    const attachmentsDetailsPayload: ICommentAttachmentDetails[] = [];

    setUploadingAttachment(true);

    try {
      if (attachment.type === ITab.FileFromConnect) {
        // Handle files from Trimble Connect
        const connectFiles = Array.isArray(attachmentDetail)
          ? attachmentDetail
          : [attachmentDetail];
        for (const fileObject of connectFiles) {
          // Check for duplicates
          if (currentAttachments.some((att) => att.id === fileObject.id)) {
            continue;
          }

          const imgUrl =
            fileObject.thumbnailUrl?.[0] || fileObject.thumbnail || '';
          const attachmentItem: ICommentAttachments = {
            title: fileObject.name,
            type: 'FILE',
            imgUrl,
            id: fileObject.id,
          };
          attachmentsPayload.push(attachmentItem);

          try {
            const base64ImgUrl = await buildAuthImg(imgUrl);
            const attachmentDetail: ICommentAttachmentDetails = {
              snapshot: {
                snapshotType: fileObject.name.split('.').pop() || 'png',
                snapshotData:
                  typeof base64ImgUrl === 'string'
                    ? base64ImgUrl.split(',')[1]
                    : '',
              },
            };
            attachmentsDetailsPayload.push(attachmentDetail);
          } catch (error) {
            console.error('Error processing thumbnail:', error);
            // Add placeholder details if thumbnail processing fails
            const attachmentDetail: ICommentAttachmentDetails = {
              snapshot: {
                snapshotType: fileObject.name.split('.').pop() || 'png',
                snapshotData: '',
              },
            };
            attachmentsDetailsPayload.push(attachmentDetail);
          }
        }

        // Apply changes after processing all files
        setTimeout(() => {
          setAttachments([...currentAttachments, ...attachmentsPayload]);
          setAttachmentsDetails([
            ...attachmentsDetails(),
            ...attachmentsDetailsPayload,
          ]);
          setShowAttachmentPicker(false);
          setUploadingAttachment(false);
        }, 2000); // eslint-disable-line no-magic-numbers -- Delay for attachment processing
      } else {
        // Fallback for other attachment types
        if (
          filesList.selectedObject &&
          Array.isArray(filesList.selectedObject)
        ) {
          const newAttachments = [
            ...attachments(),
            ...filesList.selectedObject,
          ];
          const newAttachmentsDetails = [
            ...attachmentsDetails(),
            ...filesList.selectedObject,
          ];

          setAttachments(newAttachments);
          setAttachmentsDetails(newAttachmentsDetails);
        }

        setShowAttachmentPicker(false);
        setUploadingAttachment(false);
      }
    } catch (error) {
      console.error('Error processing attachments:', error);
      setUploadingAttachment(false);
      setShowAttachmentPicker(false);
    }
  };

  const handleCloseAttachmentPicker = (): void => {
    setShowAttachmentPicker(false);
  };

  const handlePreviewClick = (event: MouseEvent): void => {
    event.stopPropagation();

    const customEvent = new CustomEvent('comment-preview-requested', {
      detail: {
        comment: props.comment,
        originalEvent: event,
      },
      bubbles: true,
    });
    (event.target as HTMLElement)?.dispatchEvent(customEvent);

    props.getCommentDetail(props.comment);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div class="comment-wrapper">
      <style>{`
        .thumbnail-image {
          width: 24px !important;
          height: 24px !important;
          object-fit: cover;
          border-radius: 50%;
        }
        .avatar.micro-aligned .icon-font.tc-icon-spinner-dark {
          width: 24px;
          height: 24px;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
      <div
        id={`des-container${props.comment.id}`}
        style={{ display: 'none' }}
      />

      <Show when={!isCurrentUser()}>
        {/* Other User's Comment */}
        <li class="list-item group mb-2" data-cy={props.comment.description}>
          <div class="group-items px-0">
            <div class="user-thumbnail">
              <div class="row-center">
                <div
                  class="mr-1 avatar micro-aligned borderless"
                  style={{
                    width: '24px',
                    height: '24px',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'min-width': '24px',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'min-height': '24px',
                  }}
                >
                  {/* @ts-expect-error - tc-auth-image is a web component defined at runtime */}
                  <tc-auth-image
                    src={commentAuthorThumbnail()}
                    errImg={fallbackImageUrl}
                    class="thumbnail-image"
                    access-token={accessToken}
                  />
                </div>
              </div>
            </div>
            <div class="block">
              <span class="user-name">
                {`${props.comment.createdBy?.firstName ?? ''} ${props.comment.createdBy?.lastName ?? ''}`.trim()}
              </span>
            </div>
          </div>
          <div class="ml-4">
            <p class="text-meta">{props.comment.description}</p>

            <Show
              when={
                !commentAttachmentsQuery.isLoading &&
                commentAttachmentsQuery.data &&
                commentAttachmentsQuery.data.length > 0 &&
                attachmentThumbnailBase64()
              }
            >
              <div class="comment-attachment-container mt-1">
                <div class="thumbnail medium mb-1">
                  {/* @ts-expect-error - tc-image is a web component defined at runtime */}
                  <tc-image
                    src={attachmentThumbnailBase64()}
                    errImg={fallbackImageUrl}
                    class="comment-attachment-image"
                  />
                </div>
                <Show when={!props.isPreviewOn}>
                  <a onClick={handlePreviewClick} href="#" class="preview-link">
                    <span
                      class="preview-overlay bg-gray_10"
                      title={translate('TC.Common.Preview')}
                    >
                      <i class="icon-font i16 tc-icon-zoom-in mx-0 preview-icon-color" />
                    </span>
                  </a>
                </Show>
              </div>
            </Show>

            <div class="small text-meta pt-1">
              {formatDate(props.comment.modifiedOn ?? '')}
            </div>
          </div>
        </li>
      </Show>

      <Show when={isCurrentUser()}>
        {/* Current User's Comment */}
        <li class="list-item group mb-2" data-cy={props.comment.description}>
          <div class="group-items flex-end px-0">
            <div class="mr-1">
              <span class="user-name">{translate('TC.Common.You')}</span>
            </div>
            <div class="user-thumbnail">
              <div class="row-center">
                <div
                  class="mr-1 avatar micro-aligned borderless"
                  style={{
                    width: '24px',
                    height: '24px',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'min-width': '24px',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'min-height': '24px',
                  }}
                >
                  {/* @ts-expect-error - tc-auth-image is a web component defined at runtime */}
                  <tc-auth-image
                    src={currentUserThumbnail()}
                    errImg={fallbackImageUrl}
                    class="thumbnail-image"
                    access-token={accessToken}
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="mr-4">
            <div class="bg-gray_light px-1 py-1">
              <Show when={!editMode()}>
                <p class="text-meta">{props.comment.description}</p>
              </Show>

              {/* Show existing attachments only when not in edit mode */}
              <Show
                when={
                  !editMode() &&
                  (hasPersistedAttachments() ||
                    (commentAttachmentsQuery.data &&
                      commentAttachmentsQuery.data.length > 0)) &&
                  !commentAttachmentsQuery.isLoading &&
                  attachmentThumbnailBase64()
                }
              >
                <div class="comment-attachment-container mt-1">
                  <div class="thumbnail medium mb-1">
                    {/* @ts-expect-error - tc-image is a web component defined at runtime */}
                    <tc-image
                      src={attachmentThumbnailBase64()}
                      errImg={fallbackImageUrl}
                      class="comment-attachment-image"
                    />
                  </div>

                  <Show when={!props.isPreviewOn}>
                    <a
                      onClick={handlePreviewClick}
                      href="#"
                      class="preview-link"
                    >
                      <span
                        class="preview-overlay bg-gray_10"
                        title={translate('TC.Common.Preview')}
                      >
                        <i class="icon-font i16 tc-icon-zoom-in mx-0 preview-icon-color" />
                      </span>
                    </a>
                  </Show>
                </div>
              </Show>

              <div class="mt-1">
                <textarea
                  id={props.comment.id}
                  class="comment-add mb-1"
                  placeholder={translate('TC.Common.Description')}
                  style={editMode() ? {} : { display: 'none' }}
                  onInput={onDescriptionUpdated}
                  value={description()}
                  maxLength={1024}
                  ref={(el) => {
                    if (el && editMode()) {
                      el.value = description() || '';
                    }
                  }}
                />

                <Show when={editMode()}>
                  <AttachmentListInEdit
                    attachments={attachments()}
                    onRemoveAttachment={removeAttachment}
                    accessToken={accessToken || undefined}
                  />
                  <div class="row-distribute">
                    <div>
                      <button
                        class={`button tertiary icon-circle icon-small ${
                          uploadingAttachment() ? 'disabled' : ''
                        }`}
                        onClick={openAttachmentPicker}
                        disabled={uploadingAttachment()}
                        type="button"
                        title="Attach file"
                      >
                        <i class="icon-font i16 tc-icon-attach-file" />
                      </button>
                    </div>
                    <div class="d-flex justify-content-end">
                      <button
                        class="button link-secondary foot-button small"
                        onClick={cancelEditing}
                        disabled={uploadingAttachment()}
                        type="button"
                      >
                        {translate('TC.Common.Cancel')}
                      </button>
                      <button
                        class={`button primary foot-button small ml-2 ${
                          inProgress() ? 'icon-left' : ''
                        }`}
                        disabled={
                          !isCommentChanged() ||
                          inProgress() ||
                          uploadingAttachment()
                        }
                        onClick={updateCommentText}
                        type="button"
                      >
                        {translate('TC.Common.Save')}
                      </button>
                    </div>
                  </div>
                </Show>
              </div>
            </div>
            <div class="row-distribute">
              <div class="small text-meta">
                {formatDate(props.comment.modifiedOn ?? '')}
              </div>
              <div class="d-flex">
                <button
                  class="button link-primary small"
                  onClick={() => selectAction('delete')}
                  disabled={!props.isCommentActionAllowed('COMMENT_DELETE')}
                  type="button"
                >
                  {translate('TC.Common.Delete')}
                </button>
                <button
                  class="button link-primary small"
                  onClick={() => selectAction('edit')}
                  disabled={!props.isCommentActionAllowed('COMMENT_UPDATE')}
                  type="button"
                >
                  {translate('TC.Common.Edit')}
                </button>
              </div>
            </div>
          </div>
        </li>
      </Show>

      <Show when={updateCommentFailed()}>
        <div class="alert error">
          {translate('TC.Error.Comments.UpdateFailed')}
        </div>
      </Show>

      {/* AttachmentPicker Integration */}
      <Show when={showAttachmentPicker()}>
        <AttachmentPicker
          addAttachment={handleAddAttachment}
          allowedAttachmentTypes={'FILE_FROM_CONNECT'}
          onClose={handleCloseAttachmentPicker}
          inProgressFlag={inProgress()}
          disableMultiSelect={true}
          is3DEmbbedded={true}
          projectName={props.projectName || 'Project'}
          projectId={props.projectId}
          accessToken={accessToken || undefined}
          tcpsApiDomain={tcpsApiDomain || undefined}
          allowedFileExtensions={['.jpg', '.png']}
          allowedUploadFileExt={['image/png', 'image/jpeg']}
        />
      </Show>
    </div>
  );
};

export default Comment;
